import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight, HelpCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useCapabilities } from '../../contexts/CapabilitiesContext';
import api from '../../services/api';
import { ADMIN_SECTIONS, CLIENT_SECTIONS, isNavActive, visibleSections } from './navigation';
import './Layout.css';

// O ícone do GyMag, para os ginásios que ainda não escolheram o seu: antes era um emoji.
const DEFAULT_ICON = '/gymag-icon.svg';

const parseSeen = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return 0;
    const t = Date.parse(raw);
    return Number.isFinite(t) ? t : 0;
  } catch {
    return 0;
  }
};

const BADGE_TITLES = { messages: 'mensagens', notifications: 'notificações' };

const Sidebar = ({ isCollapsed, setIsCollapsed, userType = 'client', isMobileOpen, onCloseMobile }) => {
  const { user, isAdmin, hasPermission } = useAuth();
  const { isEnabled } = useCapabilities();
  const location = useLocation();
  const [siteSettings, setSiteSettings] = useState({ name: 'GyMag', icon: DEFAULT_ICON });
  const [badges, setBadges] = useState({ messages: 0, notifications: 0, mybookings: 0, workout: 0, mealplan: 0, billing: 0 });

  const adminMenu = isAdmin && userType === 'admin';
  const sections = visibleSections(adminMenu ? ADMIN_SECTIONS : CLIENT_SECTIONS, { hasPermission, isEnabled, isAdmin });

  useEffect(() => {
    const loadSettings = () => {
      const stored = localStorage.getItem('siteSettings');
      if (stored) {
        try { setSiteSettings(JSON.parse(stored)); } catch (e) { /* definições corrompidas: fica o padrão */ }
      }
    };
    loadSettings();
    window.addEventListener('settingsUpdated', loadSettings);
    return () => window.removeEventListener('settingsUpdated', loadSettings);
  }, []);

  // Os números do menu: só se perguntam para os módulos que o ginásio tem ligados (um módulo desligado responderia 403 a cada 30 segundos).
  const flags = {
    messages: isEnabled('Messaging'),
    notifications: isEnabled('Notifications'),
    bookings: isEnabled('Reservations'),
    workout: isEnabled('TrainingPlans'),
    mealplan: isEnabled('MealPlans'),
    billing: isEnabled('Billing'),
  };
  const flagKey = Object.values(flags).join(',');

  useEffect(() => {
    if (!user || adminMenu) return undefined; // Só carrega para clientes

    const safe = (promise, fallback) => promise.catch(() => ({ data: fallback }));

    const loadBadges = async () => {
      try {
        const seenWorkoutAt = parseSeen('seen.workoutAt');
        const seenBillingAt = parseSeen('seen.billingAt');
        const next = { messages: 0, notifications: 0, mybookings: 0, workout: 0, mealplan: 0, billing: 0 };
        const jobs = [];

        if (flags.messages) jobs.push(safe(api.get('/Messages/unread-count'), 0).then((r) => { next.messages = r.data || 0; }));
        if (flags.notifications) {
          jobs.push(safe(api.get('/Notifications/unread-count'), 0).then((r) => { next.notifications = typeof r.data === 'number' ? r.data : (r.data?.count || 0); }));
        }
        if (flags.bookings) {
          jobs.push(safe(api.get('/bookings'), []).then((r) => {
            const data = Array.isArray(r.data) ? r.data : [];
            next.mybookings = data.filter((b) => !b.isCancelled && new Date(b.slotStartTime) >= new Date()).length;
          }));
        }
        if (flags.workout) {
          jobs.push(safe(api.get('/workoutplans'), []).then((r) => {
            const active = (Array.isArray(r.data) ? r.data : []).filter((w) => w?.isActive);
            next.workout = seenWorkoutAt > 0
              ? active.filter((w) => { const t = Date.parse(w?.updatedAt || w?.createdAt || w?.startDate || ''); return Number.isFinite(t) && t > seenWorkoutAt; }).length
              : active.length;
          }));
        }
        if (flags.mealplan) jobs.push(safe(api.get('/MealPlans'), []).then((r) => { next.mealplan = Array.isArray(r.data) ? r.data.length : 0; }));
        if (flags.billing) {
          jobs.push(safe(api.get('/billing/my/invoices'), []).then((r) => {
            const pending = (Array.isArray(r.data) ? r.data : []).filter((f) => f?.status !== 'Paid' && f?.status !== 'Cancelled' && f?.status !== 'Draft');
            next.billing = seenBillingAt > 0
              ? pending.filter((f) => { const t = Date.parse(f?.issueDate || f?.dueDate || f?.paidAt || ''); return Number.isFinite(t) && t > seenBillingAt; }).length
              : pending.length;
          }));
        }

        await Promise.all(jobs);
        setBadges(next);
      } catch (error) {
        console.error('Erro ao carregar badges:', error);
      }
    };

    loadBadges();
    const interval = setInterval(loadBadges, 30000);
    window.addEventListener('updateBadges', loadBadges);
    return () => {
      clearInterval(interval);
      window.removeEventListener('updateBadges', loadBadges);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, adminMenu, flagKey]);

  // Render icon - supports emoji or image URL
  const renderIcon = () => {
    // O 💪 era o valor por omissão guardado nas definições de cada ginásio: quase todos o têm,
    // sem o terem escolhido, e com ele o ícone novo nunca chegava a aparecer. Quem quiser mesmo
    // um emoji escolhe outro; o 💪 passa a ser "ainda não escolhi".
    if (siteSettings.icon === '💪') return <img src={DEFAULT_ICON} alt="GyMag" className="logo-img" />;

    // Um caminho do próprio site (/gymag-icon.svg) também é uma imagem, não um emoji.
    if (siteSettings.icon && (siteSettings.icon.startsWith('http') || siteSettings.icon.startsWith('data:') || siteSettings.icon.startsWith('/'))) {
      return <img src={siteSettings.icon} alt="Logo" className="logo-img" />;
    }
    return siteSettings.icon || <img src={DEFAULT_ICON} alt="GyMag" className="logo-img" />;
  };

  // On mobile, a collapsed sidebar is too narrow and feels broken.
  // When the mobile menu is open, force the expanded UI.
  const isActuallyCollapsed = isCollapsed && !isMobileOpen;

  const handleNavClick = () => {
    if (isMobileOpen && typeof onCloseMobile === 'function') onCloseMobile();
  };

  return (
    <aside className={`sidebar ${isActuallyCollapsed ? 'collapsed' : ''} ${isMobileOpen ? 'mobile-open' : ''}`} aria-label="Menu principal">
      <div className="sidebar-header">
        <div className="sidebar-logo">
          <span className="logo-icon">{renderIcon()}</span>
          {!isActuallyCollapsed && <span className="logo-text">{siteSettings.name}</span>}
        </div>
        <button
          className="sidebar-toggle"
          onClick={() => setIsCollapsed(!isCollapsed)}
          title={isCollapsed ? 'Expandir menu' : 'Recolher menu'}
          aria-label={isCollapsed ? 'Expandir menu' : 'Recolher menu'}
        >
          {isCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>
      </div>

      <nav className="sidebar-nav">
        {sections.map((section, si) => (
          <div key={section.title || `s${si}`} className="sidebar-section">
            {section.title && !isActuallyCollapsed && (
              <div className="sidebar-section-title">{section.title}</div>
            )}
            <ul className="sidebar-menu">
              {section.items.map((item) => {
                const badgeCount = item.badge ? badges[item.badge] : 0;
                const showBadge = badgeCount > 0 && userType === 'client';
                const IconComp = item.Icon;
                const active = isNavActive(item, location.pathname, location.search);

                return (
                  <li key={item.path + item.label}>
                    <NavLink
                      to={item.path}
                      className={() => `sidebar-link ${active ? 'active' : ''}`}
                      aria-current={active ? 'page' : undefined}
                      title={isActuallyCollapsed ? item.label : ''}
                      onClick={handleNavClick}
                    >
                      <span className="sidebar-icon">
                        {IconComp ? <IconComp size={18} strokeWidth={1.75} /> : null}
                      </span>
                      {!isActuallyCollapsed && <span className="sidebar-label">{item.label}</span>}
                      {showBadge && (
                        <span className="sidebar-badge" title={`${badgeCount} ${BADGE_TITLES[item.badge] || 'itens'}`}>
                          {badgeCount > 99 ? '99+' : badgeCount}
                        </span>
                      )}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      {/* Botão Ajuda — base de conhecimento partilhada */}
      <a
        href="https://baseconhecimento.magweb.ddnsfree.com"
        target="_blank"
        rel="noopener noreferrer"
        className="sidebar-link"
        style={{ marginTop: 'auto', borderTop: '1px solid rgba(255,255,255,0.06)' }}
        title="Ajuda e documentação"
      >
        <span className="sidebar-icon">
          <HelpCircle size={18} strokeWidth={1.75} />
        </span>
        {!isActuallyCollapsed && <span className="sidebar-label">Ajuda</span>}
      </a>
    </aside>
  );
};

export default Sidebar;
